import { a as list, c as getSettings, i as markAllRead, l as deleteOne, n as notify, o as getUnreadCount, r as markRead, s as getUnread, t as updateSettings, u as deleteByScope } from "./updateSettings2.js";
import { mutationOptions, queryOptions, useMutation, useQuery } from "@tanstack/react-query";
//#region src/__generated__/notification/internal/hooks/useDeleteByScope.ts
const deleteByScopeMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification" }];
function deleteByScopeMutationOptions(config = {}) {
	const mutationKey = deleteByScopeMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ query }) => {
			const { data } = await deleteByScope({
				...config,
				query,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Удалить уведомления текущего пользователя (прочитанные/непрочитанные/все)
* {@link /api/mdm/internal/notification/api/notification}
*/
function useDeleteByScope(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? deleteByScopeMutationKey();
	const baseOptions = deleteByScopeMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useDeleteOne.ts
const deleteOneMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification/:notificationId" }];
function deleteOneMutationOptions(config = {}) {
	const mutationKey = deleteOneMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await deleteOne({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Удалить одно уведомление
* {@link /api/mdm/internal/notification/api/notification/:notificationId}
*/
function useDeleteOne(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? deleteOneMutationKey();
	const baseOptions = deleteOneMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useGetSettings.ts
const getSettingsQueryKey = () => [{ url: "/api/mdm/internal/notification/api/notification/settings" }];
function getSettingsQueryOptions(config = {}) {
	const queryKey = getSettingsQueryKey();
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getSettings({
				...config,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Получить настройку получения уведомлений по email
* {@link /api/mdm/internal/notification/api/notification/settings}
*/
function useGetSettings(options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const queryKey = resolvedOptions?.queryKey ?? getSettingsQueryKey();
	const queryResult = useQuery({
		...getSettingsQueryOptions(config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useGetUnread.ts
const getUnreadQueryKey = () => [{ url: "/api/mdm/internal/notification/api/notification/unread" }];
function getUnreadQueryOptions(config = {}) {
	const queryKey = getUnreadQueryKey();
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getUnread({
				...config,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Получить непрочитанные уведомления текущего пользователя
* {@link /api/mdm/internal/notification/api/notification/unread}
*/
function useGetUnread(options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const queryKey = resolvedOptions?.queryKey ?? getUnreadQueryKey();
	const queryResult = useQuery({
		...getUnreadQueryOptions(config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useGetUnreadCount.ts
const getUnreadCountQueryKey = () => [{ url: "/api/mdm/internal/notification/api/notification/unread-count" }];
function getUnreadCountQueryOptions(config = {}) {
	const queryKey = getUnreadCountQueryKey();
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getUnreadCount({
				...config,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Получить количество непрочитанных уведомлений текущего пользователя
* {@link /api/mdm/internal/notification/api/notification/unread-count}
*/
function useGetUnreadCount(options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const queryKey = resolvedOptions?.queryKey ?? getUnreadCountQueryKey();
	const queryResult = useQuery({
		...getUnreadCountQueryOptions(config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useList.ts
const listQueryKey = ({ query } = {}) => [{ url: "/api/mdm/internal/notification/api/notification" }, ...query ? [query] : []];
function listQueryOptions({ query } = {}, config = {}) {
	const queryKey = listQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await list({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Получить список уведомлений текущего пользователя (курсорная пагинация)
* {@link /api/mdm/internal/notification/api/notification}
*/
function useList({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? listQueryKey(resolvedParams);
	const queryResult = useQuery({
		...listQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useMarkAllRead.ts
const markAllReadMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification/read-all" }];
function markAllReadMutationOptions(config = {}) {
	const mutationKey = markAllReadMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async (_) => {
			const { data } = await markAllRead({
				...config,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Отметить все уведомления текущего пользователя как прочитанные
* {@link /api/mdm/internal/notification/api/notification/read-all}
*/
function useMarkAllRead(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? markAllReadMutationKey();
	const baseOptions = markAllReadMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useMarkRead.ts
const markReadMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification/:notificationId/read" }];
function markReadMutationOptions(config = {}) {
	const mutationKey = markReadMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await markRead({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Отметить уведомление как прочитанное
* {@link /api/mdm/internal/notification/api/notification/:notificationId/read}
*/
function useMarkRead(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? markReadMutationKey();
	const baseOptions = markReadMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useNotify.ts
const notifyMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification" }];
function notifyMutationOptions(config = {}) {
	const mutationKey = notifyMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ body }) => {
			const { data } = await notify({
				...config,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Отправить уведомление по событию
* {@link /api/mdm/internal/notification/api/notification}
*/
function useNotify(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? notifyMutationKey();
	const baseOptions = notifyMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/notification/internal/hooks/useUpdateSettings.ts
const updateSettingsMutationKey = () => [{ url: "/api/mdm/internal/notification/api/notification/settings" }];
function updateSettingsMutationOptions(config = {}) {
	const mutationKey = updateSettingsMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ body }) => {
			const { data } = await updateSettings({
				...config,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @summary Изменить настройку получения уведомлений по email
* {@link /api/mdm/internal/notification/api/notification/settings}
*/
function useUpdateSettings(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? updateSettingsMutationKey();
	const baseOptions = updateSettingsMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
export { useDeleteByScope as A, getSettingsQueryOptions as C, useDeleteOne as D, deleteOneMutationOptions as E, deleteByScopeMutationKey as O, getSettingsQueryKey as S, deleteOneMutationKey as T, getUnreadCountQueryOptions as _, notifyMutationOptions as a, getUnreadQueryOptions as b, markReadMutationOptions as c, markAllReadMutationOptions as d, useMarkAllRead as f, getUnreadCountQueryKey as g, useList as h, notifyMutationKey as i, deleteByScopeMutationOptions as k, useMarkRead as l, listQueryOptions as m, updateSettingsMutationOptions as n, useNotify as o, listQueryKey as p, useUpdateSettings as r, markReadMutationKey as s, updateSettingsMutationKey as t, markAllReadMutationKey as u, useGetUnreadCount as v, useGetSettings as w, useGetUnread as x, getUnreadQueryKey as y };

//# sourceMappingURL=useUpdateSettings2.js.map